import { BACKEND_URL } from "@/lib/backendUrl";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import { ArrowLeft, Copy, KeyRound, Loader2, Lock, ShieldCheck, Smartphone, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { TenantMark } from "@/components/common/TenantMark";
import { api, apiError } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { setLanguage } from "@/i18n";

const QUICK = [
  { email: "admin@demo.servecor.tech", label: "Platform Admin" },
  { email: "analyst@demo.servecor.tech", label: "Regulatory Analyst" },
  { email: "compliance@demo.servecor.tech", label: "Compliance Officer" },
  { email: "finance@demo.servecor.tech", label: "Finance Manager" },
  { email: "auditor@demo.servecor.tech", label: "Auditor (read-only)" },
];

const BTN = "w-full rounded-full bg-primary text-primary-foreground shadow-sm shadow-primary/25 transition-transform duration-150 hover:bg-primary/90 active:scale-[0.98]";

export default function Login() {
  const { login, adoptSession } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t, i18n: inst } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState("credentials"); // credentials | challenge | enroll | recovery-codes
  const [email, setEmail] = useState("admin@demo.servecor.tech");
  const [password, setPassword] = useState("Custodia@2026");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [newPassword, setNewPassword] = useState("");
  const [againPassword, setAgainPassword] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [tenant, setTenant] = useState(null);
  const tenantLogo = tenant?.logo_url
    ? `${BACKEND_URL}${tenant.logo_url}` : "";

  useEffect(() => {
    api.get("/admin/branding/tenant")
      .then(({ data }) => setTenant(data))
      .catch(() => setTenant(null));
  }, []);

  const setFirstPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/set-first-password", {
        pending_token: pending.pending_token, password: newPassword,
      });
      await adoptSession(data);
      toast.success(t("Password set — welcome in"));
      navigate("/dashboard/executive");
    } catch (err) {
      setError(apiError(err));
    }
    setBusy(false);
  };

  const askReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail.trim() });
      setForgotSent(true);
    } catch (err) {
      setError(apiError(err));
    }
    setBusy(false);
  };

  const reset = () => {
    setStep("credentials");
    setPending(null);
    setCode("");
    setEnrollment(null);
    setUseRecovery(false);
    setError("");
    setNewPassword("");
    setAgainPassword("");
    setForgotSent(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email.trim(), password);
    if (res.ok) {
      setBusy(false);
      navigate("/dashboard/executive");
      return;
    }
    if (res.firstPassword) {
      setPending(res.firstPassword);
      setNewPassword("");
      setStep("first-password");
      setBusy(false);
      return;
    }
    if (res.mfa) {
      setPending(res.mfa);
      if (res.mfa.enrollment_required) {
        try {
          const { data } = await api.post(`/auth/mfa/enroll/start?pending_token=${encodeURIComponent(res.mfa.pending_token)}`);
          setEnrollment(data);
          setStep("enroll");
        } catch (err) {
          setError(apiError(err));
        }
      } else {
        setStep("challenge");
      }
      setBusy(false);
      return;
    }
    setBusy(false);
    setError(res.error);
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const path = useRecovery ? "recovery" : "verify";
    try {
      const { data } = await api.post(
        `/auth/mfa/${path}?pending_token=${encodeURIComponent(pending.pending_token)}`,
        { code: code.trim() },
      );
      await adoptSession(data);
      navigate("/dashboard/executive");
    } catch (err) {
      setError(apiError(err));
      setCode("");
    }
    setBusy(false);
  };

  const confirmEnrollment = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(
        `/auth/mfa/enroll/confirm?pending_token=${encodeURIComponent(pending.pending_token)}`,
        { code: code.trim() },
      );
      setRecoveryCodes(data.recovery_codes || []);
      if (data.session) await adoptSession(data.session);
      setStep("recovery-codes");
    } catch (err) {
      setError(apiError(err));
      setCode("");
    }
    setBusy(false);
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.05fr_1fr]">
      <div className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-[#241f5c] via-[#151a3d] to-[#0b1024] p-10 lg:flex">
        <div className="grain absolute inset-0" />
        <div className="relative flex items-center gap-4">
          <span className="rounded-3xl bg-[#E2E8F0] p-3 shadow-[0_10px_40px_rgba(0,0,0,0.35)]">
            <img src="/brand/servecor-mark.png" alt="Servecor"
                 data-testid="login-hero-mark"
                 className="h-32 w-auto" />
          </span>
          <div>
            <div className="font-display text-3xl font-bold tracking-tight text-white">ServeCOR</div>
            <div className="text-[13px] text-indigo-200/80">{t("Empowering Trust. Enabling Impact")}</div>
          </div>
        </div>

        <div className="relative max-w-xl space-y-6">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-indigo-700 dark:text-indigo-300/80">
            {t("Global Operating & Regulatory Intelligence")}
          </div>
          <h1 className="font-display text-4xl font-bold leading-[1.08] tracking-tight text-white xl:text-5xl">
            {t("Know what applies.")}
            <br />
            <span className="bg-gradient-to-r from-indigo-300 to-teal-300 bg-clip-text text-transparent">{t("Prove why.")}</span>{" "}
            {t("Act correctly.")}
            <br />
            {t("Keep the evidence.")}
          </h1>
          <p className="text-sm leading-relaxed text-indigo-100/70">
            {t("The regulatory control plane for charities and NPOs. Every donation, disbursement and posting is evaluated against versioned, evidence-backed rules across a twelve-layer applicability cascade — and the decision trace is kept forever.")}
          </p>
          <div className="grid grid-cols-3 gap-3 pt-2">
            {[
              ["12", "Cascade layers"],
              ["8", "Lifecycle states"],
              ["50", "Jurisdictions"],
            ].map(([v, l]) => (
              <div key={l} className="rounded-xl border border-white/10 bg-white/5 px-3 py-3 backdrop-blur">
                <div className="font-display text-xl font-bold text-teal-700 dark:text-teal-300">{v}</div>
                <div className="mt-1 text-[11px] text-indigo-100/60">{t(l)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-indigo-700 dark:text-indigo-300/50">
          <Lock className="h-3 w-3" /> TLS · bcrypt · TOTP · tamper-evident audit chain
        </div>
        <div data-testid="login-product-credit" className="relative mt-2 text-[11px] text-indigo-700/80 dark:text-indigo-200/60">
          A product of SuperNEO Pvt. Ltd.
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div className="flex items-center gap-3" data-testid="login-tenant">
              {tenant && (
                <TenantMark logoUrl={tenantLogo} name={tenant.organisation}
                            monogram={tenant.monogram} size={56}
                            testId="login-tenant-logo" />
              )}
              <div className="min-w-0">
                <div className="kicker mb-0.5">{t("Signing in to")}</div>
                <div className="truncate text-[15px] font-semibold text-foreground"
                     data-testid="login-tenant-name">
                  {tenant?.organisation || "\u00a0"}
                </div>
                {tenant?.tagline && (
                  <div className="truncate text-[11px] text-muted-foreground"
                       data-testid="login-tenant-tagline">{tenant.tagline}</div>
                )}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setLanguage(inst.language === "hi" ? "en" : "hi")}
                data-testid="login-lang-toggle"
                className="rounded-full border border-border bg-card/80 px-3 py-1.5 text-[11px] font-medium text-foreground/80 backdrop-blur transition-colors duration-200 hover:border-primary/50 hover:text-primary"
              >
                {inst.language === "hi" ? "English" : "हिन्दी"}
              </button>
              <button
                type="button"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                data-testid="login-theme-toggle"
                className="rounded-full border border-border bg-card/80 px-3 py-1.5 text-[11px] font-medium text-foreground/80 backdrop-blur transition-colors duration-200 hover:border-primary/50 hover:text-primary"
              >
                {theme === "dark" ? t("Light theme") : t("Dark theme")}
              </button>
            </div>
          </div>

          {step === "credentials" && (
            <>
              <div className="kicker mb-2">{t("Secure sign-in")}</div>
              <h2 className="mb-6 font-display text-xl font-semibold text-foreground">{t("Access the Work Space")}</h2>

              <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs text-muted-foreground">{t("Work email")}</Label>
                  <Input id="email" type="email" value={email} autoComplete="username"
                         onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input"
                         className="bg-muted/40 text-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-xs text-muted-foreground">{t("Password")}</Label>
                  <Input id="password" type="password" value={password} autoComplete="current-password"
                         onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input"
                         className="bg-muted/40 text-sm" />
                </div>

                {error && (
                  <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-300"
                       data-testid="login-error">
                    {error}
                  </div>
                )}

                <Button type="submit" disabled={busy} data-testid="login-submit-button" className={BTN}>
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
                  {t("Sign in")}
                </Button>
              </form>

              <button type="button" data-testid="forgot-password-link"
                      onClick={() => { setForgotEmail(email); setForgotSent(false);
                        setError(""); setStep("forgot"); }}
                      className="mt-3 text-[11.5px] text-primary hover:underline">
                {t("Forgotten your password?")}
              </button>

              <div className="mt-8 border-t border-border pt-5">
                <div className="kicker mb-3 flex items-center gap-1.5">
                  <ShieldCheck className="h-3 w-3" /> {t("Demo role presets")}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {QUICK.map((q) => (
                    <button key={q.email} type="button"
                            onClick={() => { setEmail(q.email); setPassword("Custodia@2026"); }}
                            data-testid={`login-preset-${q.email.split("@")[0]}`}
                            className="rounded-full border border-border px-2.5 py-1 text-[11px] text-foreground/70 transition-colors duration-150 hover:border-primary/60 hover:text-primary">
                      {t(q.label)}
                    </button>
                  ))}
                </div>
                <p className="mt-3 font-mono text-[10px] text-foreground/60">{t("Shared demo password:")} Custodia@2026</p>
              </div>
            </>
          )}

          {step === "first-password" && (
            <div data-testid="first-password-step">
              <div className="kicker mb-2 flex items-center gap-1.5">
                <Lock className="h-3 w-3" /> {t("One last step")}
              </div>
              <h2 className="mb-1 font-display text-xl font-semibold text-foreground">
                {t("Set your own password")}
              </h2>
              <p className="mb-5 text-sm text-muted-foreground">
                {t("You signed in with a temporary password your administrator issued. Choose something only you know — at least 8 characters.")}
              </p>
              <form onSubmit={setFirstPassword} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">{t("New password")}</Label>
                  <Input type="password" value={newPassword} autoFocus
                         onChange={(e) => setNewPassword(e.target.value)}
                         data-testid="first-password-input" className="bg-muted/40 text-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">{t("Type it again")}</Label>
                  <Input type="password" value={againPassword}
                         onChange={(e) => setAgainPassword(e.target.value)}
                         data-testid="first-password-confirm" className="bg-muted/40 text-sm" />
                </div>
                {error && (
                  <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-300"
                       data-testid="first-password-error">{error}</div>
                )}
                <Button type="submit" className={BTN} data-testid="first-password-save"
                        disabled={busy || newPassword.length < 8 || newPassword !== againPassword}>
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <KeyRound className="mr-2 h-4 w-4" />} {t("Set it and sign in")}
                </Button>
              </form>
              <button onClick={reset} data-testid="first-password-back"
                      className="mt-6 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-3 w-3" /> {t("Back to sign in")}
              </button>
            </div>
          )}

          {step === "forgot" && (
            <div data-testid="forgot-password-step">
              <div className="kicker mb-2 flex items-center gap-1.5">
                <KeyRound className="h-3 w-3" /> {t("Password reset")}
              </div>
              <h2 className="mb-1 font-display text-xl font-semibold text-foreground">
                {forgotSent ? t("Check your email") : t("Forgotten your password?")}
              </h2>
              <p className="mb-5 text-sm text-muted-foreground">
                {forgotSent
                  ? t("If that account exists, a link to set a new password is on its way. It is good for one hour.")
                  : t("Give us the email you sign in with and we will send you a link to set a new password.")}
              </p>
              {!forgotSent && (
                <form onSubmit={askReset} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">{t("Work email")}</Label>
                    <Input type="email" value={forgotEmail} autoFocus
                           onChange={(e) => setForgotEmail(e.target.value)}
                           data-testid="forgot-email-input" className="bg-muted/40 text-sm" />
                  </div>
                  {error && (
                    <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-300"
                         data-testid="forgot-error">{error}</div>
                  )}
                  <Button type="submit" className={BTN} data-testid="forgot-submit"
                          disabled={busy || !forgotEmail.includes("@")}>
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("Send me a link")}
                  </Button>
                </form>
              )}
              <button onClick={reset} data-testid="forgot-back"
                      className="mt-6 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-3 w-3" /> {t("Back to sign in")}
              </button>
            </div>
          )}

          {step === "challenge" && (            <div data-testid="mfa-challenge">
              <div className="kicker mb-2 flex items-center gap-1.5"><Smartphone className="h-3 w-3" /> {t("Step 2 of 2")}</div>
              <h2 className="mb-1 font-display text-xl font-semibold text-foreground">
                {useRecovery ? t("Use a recovery code") : t("Enter your one-time code")}
              </h2>
              <p className="mb-6 text-sm text-muted-foreground">
                {useRecovery
                  ? t("Each recovery code works once. We will retire it after use.")
                  : t("Open your authenticator app and enter the 6-digit code for {{email}}.", { email: pending?.email })}
              </p>

              <form onSubmit={verify} className="space-y-4">
                <Input value={code} onChange={(e) => setCode(e.target.value)} autoFocus
                       placeholder={useRecovery ? "xxxx-xxxxxx" : "123456"}
                       inputMode={useRecovery ? "text" : "numeric"}
                       maxLength={useRecovery ? 24 : 6}
                       data-testid="mfa-code-input"
                       className="bg-muted/40 text-center font-mono text-lg tracking-[0.35em]" />

                {error && (
                  <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-300"
                       data-testid="mfa-error">{error}</div>
                )}

                <Button type="submit" disabled={busy || code.trim().length < 6} data-testid="mfa-verify-button" className={BTN}>
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("Verify & continue")}
                </Button>
              </form>

              <div className="mt-6 flex items-center justify-between text-[11px]">
                <button onClick={reset} data-testid="mfa-back-button"
                        className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
                  <ArrowLeft className="h-3 w-3" /> {t("Back to sign in")}
                </button>
                <button onClick={() => { setUseRecovery(!useRecovery); setCode(""); setError(""); }}
                        data-testid="mfa-toggle-recovery"
                        className="text-primary hover:underline">
                  {useRecovery ? t("Use authenticator code") : t("Lost your device?")}
                </button>
              </div>
            </div>
          )}

          {step === "enroll" && (
            <div data-testid="mfa-enroll">
              <div className="kicker mb-2 flex items-center gap-1.5"><ShieldCheck className="h-3 w-3" /> {t("Enrolment required")}</div>
              <h2 className="mb-1 font-display text-xl font-semibold text-foreground">{t("Set up two-factor authentication")}</h2>
              <p className="mb-5 text-sm text-muted-foreground">
                {t("Your administrator requires a one-time code for this account. Scan the QR with Google Authenticator, Authy or 1Password, then enter the code it shows.")}
              </p>

              {enrollment && (
                <div className="panel mb-4 flex flex-col items-center gap-3 p-4">
                  <div className="rounded-xl bg-white p-3">
                    <QRCode value={enrollment.provisioning_uri} size={148} data-testid="mfa-qr" />
                  </div>
                  <button type="button" data-testid="mfa-secret-copy"
                          onClick={() => { navigator.clipboard?.writeText(enrollment.secret); toast.success(t("Secret copied")); }}
                          className="inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground hover:text-primary">
                    <Copy className="h-3 w-3" /> {enrollment.secret}
                  </button>
                </div>
              )}

              <form onSubmit={confirmEnrollment} className="space-y-4">
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456"
                       inputMode="numeric" maxLength={6} data-testid="mfa-enroll-code-input"
                       className="bg-muted/40 text-center font-mono text-lg tracking-[0.35em]" />
                {error && (
                  <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-300"
                       data-testid="mfa-enroll-error">{error}</div>
                )}
                <Button type="submit" disabled={busy || code.trim().length !== 6} data-testid="mfa-enroll-confirm-button" className={BTN}>
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("Confirm & activate")}
                </Button>
              </form>

              <button onClick={reset} data-testid="mfa-enroll-back"
                      className="mt-6 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-3 w-3" /> {t("Back to sign in")}
              </button>
            </div>
          )}

          {step === "recovery-codes" && (
            <div data-testid="mfa-recovery-codes">
              <div className="kicker mb-2">{t("Two-factor is active")}</div>
              <h2 className="mb-1 font-display text-xl font-semibold text-foreground">{t("Save your recovery codes")}</h2>
              <p className="mb-5 text-sm text-muted-foreground">
                {t("Store these somewhere safe. Each one signs you in once if you lose your authenticator.")}
              </p>
              <div className="panel mb-5 grid grid-cols-2 gap-2 p-4">
                {recoveryCodes.map((c) => (
                  <span key={c} className="rounded-lg bg-muted/60 px-2 py-1.5 text-center font-mono text-[12px] text-foreground">
                    {c}
                  </span>
                ))}
              </div>
              <Button data-testid="mfa-continue-button" className={BTN}
                      onClick={() => navigate("/dashboard/executive")}>
                {t("I have saved them — continue")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
